<template>
  <div class="plan-structure-tabs">
    <nav class="tabs-nav" role="tablist">
      <button
        type="button"
        role="tab"
        :aria-selected="activeTab === 'estrutura'"
        :class="['tab-item', { 'tab-item--active': activeTab === 'estrutura' }]"
        @click="onEstruturaClick"
      >
        <font-awesome-icon
          :icon="['fas', 'layer-group']"
          class="tab-icon"
          :class="{ 'tab-icon--active': activeTab === 'estrutura' }"
        />
        <span class="tab-label">Estrutura</span>
      </button>

      <button
        type="button"
        role="tab"
        :aria-selected="activeTab === 'acoes'"
        :class="['tab-item', { 'tab-item--active': activeTab === 'acoes' }]"
        @click="onAcoesClick"
      >
        <font-awesome-icon
          :icon="['fas', 'clipboard-list']"
          class="tab-icon"
          :class="{ 'tab-icon--active': activeTab === 'acoes' }"
        />
        <span class="tab-label">Ações</span>
        <span v-if="actionsTotal > 0" class="tab-badges">
          <span
            v-if="actionBadges.concluded > 0"
            class="tab-badge tab-badge--concluded"
            :title="`${actionBadges.concluded} concluídas`"
          >
            {{ actionBadges.concluded }}
          </span>
          <span
            v-if="actionBadges.inProgress > 0"
            class="tab-badge tab-badge--progress"
            :title="`${actionBadges.inProgress} em andamento`"
          >
            {{ actionBadges.inProgress }}
          </span>
          <span
            v-if="actionBadges.notStarted > 0"
            class="tab-badge tab-badge--pending"
            :title="`${actionBadges.notStarted} não iniciadas`"
          >
            {{ actionBadges.notStarted }}
          </span>
          <span
            v-if="actionBadges.delayed > 0"
            class="tab-badge tab-badge--delayed"
            :title="`${actionBadges.delayed} atrasadas`"
          >
            {{ actionBadges.delayed }}
          </span>
        </span>
      </button>
    </nav>
    <div class="tabs-underline" aria-hidden="true" />
  </div>
</template>

<script>
export default {
  name: "PlanStructureTabs",
  props: {
    activeTab: {
      type: String,
      default: "estrutura",
      validator: (v) => ["estrutura", "acoes"].includes(v),
    },
    structureDetail: {
      type: String,
      default: "",
    },
    actionBadges: {
      type: Object,
      default: () => ({
        concluded: 0,
        inProgress: 0,
        notStarted: 0,
        delayed: 0,
      }),
    },
    strategicPlanId: {
      type: [String, Number],
      default: null,
    },
    scrollTargetSelector: {
      type: String,
      default: "",
    },
  },
  computed: {
    actionsTotal() {
      const b = this.actionBadges || {};
      return (
        (b.concluded || 0) +
        (b.inProgress || 0) +
        (b.notStarted || 0) +
        (b.delayed || 0)
      );
    },
  },
  methods: {
    onEstruturaClick() {
      if (this.$route.name === "strategic_plans_show" && this.scrollTargetSelector) {
        const el = document.querySelector(this.scrollTargetSelector);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else if (this.strategicPlanId) {
        this.$router.push({
          name: "strategic_plans_show",
          params: { id: this.strategicPlanId },
        });
      } else {
        this.$router.push({ name: "strategic_plans_list" });
      }
    },
    onAcoesClick() {
      this.$router.push({
        name: "acoes_list",
        query: this.strategicPlanId
          ? { strategicPlanId: this.strategicPlanId }
          : undefined,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.plan-structure-tabs {
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  padding-top: 1.5rem;
}

.tabs-nav {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  position: relative;
}

.tab-item {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  margin: 0;
  border: none;
  border-radius: 0.5rem;
  background: #f3f4f6;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #9ca3af;
  transition: color 0.15s ease, background 0.15s ease;
  position: relative;
}

.tab-item:hover {
  color: #4b5563;
  background: #e5e7eb;
}

.tab-item--active {
  color: #1f2937;
  background: #fff;
}

.tab-item--active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  background-color: var(--primary, #1976d2);
}

.tab-icon {
  color: #9ca3af;
  font-size: 1rem;
}

.tab-item:hover .tab-icon {
  color: #6b7280;
}

.tab-icon--active {
  color: var(--primary, #1976d2) !important;
}

.tab-item--active .tab-icon {
  color: var(--primary, #1976d2);
}

.tab-label {
  white-space: nowrap;
}

.tab-detail {
  font-size: 0.8125rem;
  font-weight: 400;
  color: #9ca3af;
}

.tab-item--active .tab-detail {
  color: #6b7280;
}

.tab-badges {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.25rem;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  font-size: 0.6875rem;
  font-weight: 600;
  border-radius: 0.25rem;
  line-height: 1;
}

.tab-badge--concluded {
  background-color: #dcfce7;
  color: #166534;
}

.tab-badge--progress {
  background-color: #fef9c3;
  color: #854d0e;
}

.tab-badge--pending {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.tab-badge--delayed {
  background-color: #fee2e2;
  color: #991b1b;
}

.tabs-underline {
  height: 0;
  overflow: hidden;
}
</style>
